import React, { FC } from "react";
import { Box, Stack, Typography, Link } from "@mui/material";

import Logo from "@components/Logo";
import Social from "@components/Social";
import { Colors } from "@theme/colors-twentyfive";
import LayoutTwentyFour from "../LayoutTwentyFour";
import { uiContainerTransitionMs, uiOpacityHover } from "@src/utils";
import FeaturePopup from "../FeaturePopup";

const AiChallengeFooter: FC = () => {
  const thisYear = new Date().getFullYear();

  const linkStateDefaultStyles = {
    opacity: 1,
    transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
  };

  const linkStateHoverStyles = {
    opacity: uiOpacityHover,
  };

  const footerTextStyles = {
    textDecoration: "none",
    fontFamily: "Inter",
    fontSize: "15px",
    fontWeight: 400,
    letterSpacing: "-0.25px",
    color: Colors.PrimaryContrast,
    marginTop: "0px !important",
    marginBottom: "0px !important",
  };

  const footerLinkStyles = {
    ...footerTextStyles,
    ...linkStateDefaultStyles,
    "&:hover": {
      ...linkStateHoverStyles,
    },
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          background: Colors.PrimaryContainerDark,
          zIndex: "4",
          fontFamily: "Inter",
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: {
              xs: "center",
              md: "flex-start",
            },
            flexDirection: {
              xs: "column",
              lg: "row",
            },
            padding: {
              xs: "24px",
              md: "64px",
            },
            gap: {
              xs: "24px",
              md: "0px",
            },
          }}
        >
          <Stack
            direction="column"
            sx={{
              justifyContent: {
                xs: "center",
                md: "flex-start",
              },
            }}
          >
            <Box
              sx={{
                color: "white",
                svg: {
                  width: "160px",
                  height: "auto",
                },
                textAlign: {
                  xs: "center",
                  md: "left",
                },
              }}
            >
              <Link href="/" sx={{ color: Colors.PrimaryContrast }}>
                <Logo />
              </Link>
            </Box>
            <Typography
              variant="body2"
              sx={{
                margin: "16px 0",
                color: Colors.PrimaryContrast,
                fontSize: "18px",
                letterSpacing: "-0.6%",
                lineHeight: "1.5",
                fontWeight: 500,
                textTransform: "none",
              }}
            >
              Security guardrails for AI applications.
            </Typography>
          </Stack>

          <Stack
            sx={{
              alignItems: "center",
              gap: {
                xs: "16px",
                sm: "24px",
                xl: "30px",
              },
              flexDirection: {
                xs: "column",
                md: "row",
              },
            }}
          >
            <Link
              href="/news-events/announcements/soc2-type2-audit/"
              sx={{
                ...linkStateDefaultStyles,
                textDecoration: "none",
                "&:hover": {
                  textDecoration: "none",
                  ...linkStateHoverStyles,
                },
              }}
            >
              <Stack alignItems="center" direction="row" gap="8px">
                <Box
                  component="img"
                  src="/icons/soc2.png"
                  alt="SOC 2 Type I icon"
                  sx={{
                    height: "60px",
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: Colors.PrimaryContrast,
                    fontSize: "18px",
                  }}
                >
                  SOC 2 Type 2
                </Typography>
              </Stack>
            </Link>
            <Stack alignItems="center" direction="row" gap="8px">
              <Box
                component="img"
                src="/icons/hipaa-compliance.png"
                alt="HIPAA compliance icon"
                sx={{
                  height: "60px",
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: Colors.PrimaryContrast,
                  fontSize: "18px",
                }}
              >
                HIPAA Compliant
              </Typography>
            </Stack>
            <Stack alignItems="center" direction="row" gap="8px">
              <Box
                component="img"
                src="/icons/iso-iec-27001-compliance.png"
                alt="ISO/IEC 27001 compliance icon"
                sx={{
                  height: "60px",
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: Colors.PrimaryContrast,
                  fontSize: "18px",
                }}
              >
                ISO/IEC 27001
              </Typography>
            </Stack>
            <Stack alignItems="center" direction="row" gap="8px">
              <Box
                component="img"
                src="/icons/Sensiba-ISO-IEC-27701.png"
                alt="ISO/IEC 27701 compliance icon"
                sx={{
                  height: "60px",
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: Colors.PrimaryContrast,
                  fontSize: "18px",
                }}
              >
                ISO/IEC 27701
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent={{ xs: "flex-start", sm: "space-between" }}
          alignItems={{ xs: "center", md: "center" }}
          sx={{
            backgroundColor: Colors.PrimaryContainerDark,
            padding: {
              xs: "24px",
              md: "64px",
            },
          }}
        >
          <Stack
            sx={{
              color: Colors.PrimaryContrast,
              gap: "24px",
              flexDirection: {
                xs: "column-reverse",
                lg: "column",
              },
              justifyContent: {
                lg: "flex-start",
                xs: "space-between",
              },
            }}
          >
            <Stack
              sx={{
                flexDirection: {
                  xs: "column",
                  lg: "row",
                },
                gap: {
                  lg: "24px",
                  xs: "12px",
                },
                "*, Link, p, Typography": {
                  ...footerTextStyles,
                },
                a: {
                  opacity: 1,
                  transition: `opacity ${uiContainerTransitionMs}ms ease-in-out`,
                  "&:hover": {
                    opacity: uiOpacityHover,
                  },
                },
                alignItems: "center",
              }}
              spacing={1}
            >
              <span>© {thisYear} Pangea. All rights reserved.</span>

              <Typography>636 Ramona St, Palo Alto, CA 94301</Typography>

              <Link href="/privacy-policy/" sx={footerLinkStyles}>
                Privacy
              </Link>

              <Link href="/terms-of-use/" sx={footerLinkStyles}>
                Terms of Use
              </Link>

              <Link href="/privacy-choices/" sx={footerLinkStyles}>
                Your Privacy Choices
              </Link>

              <Link href="/contact-us/" sx={footerLinkStyles}>
                Contact us
              </Link>
            </Stack>

            <Social iconStyle="square" />
          </Stack>
        </Stack>
      </Box>

      <FeaturePopup />
    </>
  );
};

export default AiChallengeFooter;
